import {
  Archive,
  Box,
  File,
  FileText,
  Image as ImageIcon,
  Lock,
  PenTool,
  Shapes,
  Table2,
  type LucideIcon,
} from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/Avatar';
import { Badge } from '@/components/ui/Badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/Tooltip';
import { getEngStatusMeta, isEngLockedStatus, type EngFileCategory } from '@/features/engineering/model';
import type { LiteProfile } from '@/features/engineering/hooks';
import { initialsOf } from '@/components/projects/PeoplePicker';
import { cn } from '@/lib/utils';

const FILE_CATEGORY_ICON: Record<EngFileCategory, LucideIcon> = {
  cad: Box,
  mesh: Shapes,
  drawing: PenTool,
  image: ImageIcon,
  document: FileText,
  spreadsheet: Table2,
  archive: Archive,
  other: File,
};

export function FileIcon({ category, className }: { category: string; className?: string }) {
  const Icon = FILE_CATEGORY_ICON[category as EngFileCategory] ?? File;
  return <Icon className={className} strokeWidth={1.6} />;
}

/** Status chip; locked states carry a padlock so the freeze is visible at a glance. */
export function EngStatusBadge({ status, className }: { status: string; className?: string }) {
  const meta = getEngStatusMeta(status);
  return (
    <Badge variant={meta.variant} className={cn('gap-1 whitespace-nowrap', className)}>
      {isEngLockedStatus(status) && <Lock className="h-3 w-3" aria-hidden />}
      {meta.label}
    </Badge>
  );
}

export function PersonChip({
  person,
  size = 'sm',
  className,
  subtitle,
}: {
  person: LiteProfile | null | undefined;
  size?: 'xs' | 'sm' | 'md';
  className?: string;
  subtitle?: React.ReactNode;
}) {
  if (!person) return <span className={cn('text-sm text-muted-foreground', className)}>—</span>;
  const avatar = size === 'xs' ? 'h-5 w-5 text-[9px]' : size === 'md' ? 'h-8 w-8 text-xs' : 'h-6 w-6 text-[10px]';
  return (
    <span className={cn('inline-flex min-w-0 items-center gap-2', className)}>
      <Avatar className={cn(avatar, 'shrink-0')}>
        {person.avatar_url && <AvatarImage src={person.avatar_url} alt="" />}
        <AvatarFallback className={size === 'xs' ? 'text-[9px]' : size === 'md' ? 'text-xs' : 'text-[10px]'}>
          {initialsOf(person)}
        </AvatarFallback>
      </Avatar>
      <span className="min-w-0">
        <span className="block truncate text-sm text-foreground">{person.full_name ?? person.email}</span>
        {subtitle && <span className="block truncate text-xs text-muted-foreground">{subtitle}</span>}
      </span>
    </span>
  );
}

/** Overlapping avatar row for a team, with a tooltip listing names. */
export function AvatarStack({
  people,
  max = 4,
  className,
  emptyLabel = 'No team',
}: {
  people: Array<LiteProfile | null | undefined>;
  max?: number;
  className?: string;
  /** Text when nobody is listed; pass `null` to render nothing. */
  emptyLabel?: string | null;
}) {
  const list = people.filter((p): p is LiteProfile => !!p);
  if (list.length === 0) {
    return emptyLabel == null ? null : <span className={cn('text-xs text-muted-foreground', className)}>{emptyLabel}</span>;
  }
  const shown = list.slice(0, max);
  const extra = list.length - shown.length;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className={cn('inline-flex items-center', className)}>
          {shown.map((p, i) => (
            <Avatar
              key={p.id}
              className={cn('h-6 w-6 border-2 border-card text-[10px]', i > 0 && '-ml-2')}
            >
              {p.avatar_url && <AvatarImage src={p.avatar_url} alt="" />}
              <AvatarFallback className="text-[10px]">{initialsOf(p)}</AvatarFallback>
            </Avatar>
          ))}
          {extra > 0 && (
            <span className="-ml-2 inline-flex h-6 w-6 items-center justify-center rounded-full border-2 border-card bg-secondary text-[10px] font-medium text-muted-foreground">
              +{extra}
            </span>
          )}
        </span>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">
        {list.map((p) => p.full_name ?? p.email).join(', ')}
      </TooltipContent>
    </Tooltip>
  );
}

export function StatCard({
  label,
  value,
  hint,
  icon: Icon,
  className,
}: {
  label: string;
  value: React.ReactNode;
  hint?: React.ReactNode;
  icon?: typeof Lock;
  className?: string;
}) {
  return (
    <div className={cn('rounded-lg border border-border bg-card p-4 shadow-[0_1px_2px_0_hsl(124_26%_14%/0.04)]', className)}>
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
        {Icon && <Icon className="h-4 w-4 text-info" strokeWidth={1.6} />}
      </div>
      <p className="mt-2 text-2xl font-medium tracking-headline text-foreground">{value}</p>
      {hint && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

export function KV({ label, value, className }: { label: string; value: React.ReactNode; className?: string }) {
  return (
    <div className={cn('flex items-start justify-between gap-4 py-2.5', className)}>
      <span className="shrink-0 text-sm text-muted-foreground">{label}</span>
      <span className="min-w-0 text-right text-sm text-foreground">{value}</span>
    </div>
  );
}

/** Budget usage bar; turns salmon once spend passes the budget. */
export function BudgetBar({
  spentCents,
  budgetCents,
  className,
}: {
  spentCents: number;
  budgetCents: number | null;
  className?: string;
}) {
  if (!budgetCents || budgetCents <= 0) return null;
  const ratio = Math.min(spentCents / budgetCents, 1);
  const over = spentCents > budgetCents;
  return (
    <div className={cn('h-1.5 w-full overflow-hidden rounded-full bg-secondary', className)} aria-hidden>
      <div
        className={cn('h-full rounded-full transition-all', over ? 'bg-fresh-salmon-dark' : ratio > 0.85 ? 'bg-fresh-salmon' : 'bg-bright-moss-dark')}
        style={{ width: `${Math.max(ratio * 100, spentCents > 0 ? 2 : 0)}%` }}
      />
    </div>
  );
}
